import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
    AccentEnum,
    AIVoice,
    AIVoiceStyleEnum,
    AIVoiceToneEnum,
    LanguageEnum,
} from 'js/src/client';

type StoreIdType = string | number;

type NormalizedState<ItemType, IdType extends StoreIdType> = {
    byId: Record<IdType, ItemType>;
    allIds: Array<IdType>;
};

const getInitialNormalizedState = <ItemType, IdType extends StoreIdType>() => {
    const state: NormalizedState<ItemType, IdType> = {
        byId: {} as Record<IdType, ItemType>,
        allIds: [],
    };
    return state;
};

/**
 * @todo update to match type from client when endpoint is added
 */
export type LanguageItem = { id: LanguageEnum; value: LanguageEnum; display: string };
export type AccentItem = { id: AccentEnum; value: AccentEnum; display: string };
export type ToneItem = { id: AIVoiceToneEnum; value: AIVoiceToneEnum; display: string };
export type StyleItem = { id: AIVoiceStyleEnum; value: AIVoiceStyleEnum; display: string };

export type AIVoiceFilters = {
    languages: LanguageEnum[];
    style: AIVoiceStyleEnum | null;
    accent: AccentEnum | null;
    tone: AIVoiceToneEnum | null;
    searchTerm: string;
};

const getInitialFiltersState = (): AIVoiceFilters => ({
    languages: [],
    style: null,
    accent: null,
    tone: null,
    searchTerm: '',
});

const getInitialState = () => ({
    voices: getInitialNormalizedState<AIVoice, string>(),
    languages: getInitialNormalizedState<LanguageItem, string>(),
    accents: getInitialNormalizedState<AccentItem, string>(),
    tones: getInitialNormalizedState<ToneItem, string>(),
    styles: getInitialNormalizedState<StyleItem, string>(),
    filters: getInitialFiltersState(),
});
type InitialState = ReturnType<typeof getInitialState>;

export const aiVoicesSlice = createSlice({
    name: 'aiVoices',
    initialState: getInitialState(),
    reducers: {
        setAIVoices: (state: InitialState, action: PayloadAction<AIVoice[]>) => {
            const voiceIds = new Set([...state.voices.allIds]);

            const voices = action.payload;
            voices.forEach((voice) => {
                const id = voice.slug;
                state.voices.byId[id];
                voiceIds.add(id);
            });

            state.voices.allIds = Array.from(voiceIds);
        },
        setLanguages: (state: InitialState, action: PayloadAction<LanguageItem[]>) => {
            const items = action.payload;

            const ids = new Set<LanguageItem['id']>();
            items.forEach((item) => {
                const id = item.id;
                state.languages.byId[id] = item;
                ids.add(id);
            });

            state.languages.allIds = Array.from(ids);
        },
        setFilterLanguages: (state, action: PayloadAction<LanguageEnum[]>) => {
            state.filters.languages = action.payload;
            state.voices = getInitialNormalizedState();
        },
        setFilterStyle: (state, action: PayloadAction<AIVoiceStyleEnum | null>) => {
            state.filters.style = action.payload;
            state.voices = getInitialNormalizedState();
        },
        setFilterAccent: (state, action: PayloadAction<AccentEnum | null>) => {
            state.filters.accent = action.payload;
            state.voices = getInitialNormalizedState();
        },
        setFilterTone: (state, action: PayloadAction<AIVoiceToneEnum | null>) => {
            state.filters.tone = action.payload;
            state.voices = getInitialNormalizedState();
        },
        setFilterSearchTerm: (state, action: PayloadAction<string>) => {
            state.filters.searchTerm = action.payload;
            state.voices = getInitialNormalizedState();
        },
    },
});

export const {
    setAIVoices,
    setFilterLanguages,
    setFilterStyle,
    setFilterAccent,
    setFilterTone,
    setFilterSearchTerm,
} = aiVoicesSlice.actions;
